/**
 * Shop kiosk meta cache invalidation (G4 — active shop session stale).
 *
 * `paymentSurfaceReadiness` lives on GET /customer/sales-points/by-id|by-slug
 * (`shopKioskById` / `shopKioskBySlug`). Directory SSE (bank verify, listability)
 * and kiosk SSE (`kiosk_updated`) must refresh these queries so checkout pay
 * gates recover without F5.
 */

import type { QueryClient } from '@tanstack/react-query';
import { customerQueryKeys } from './queryKeys.js';

function isShopKioskMetaQueryKey(key: readonly unknown[]): boolean {
  return (
    key.length >= 4 &&
    key[0] === 'customer' &&
    key[2] === 'shop' &&
    (key[3] === 'kioskById' || key[3] === 'kioskBySlug')
  );
}

/** Prefix-invalidate every tenant shop kiosk meta query (global directory SSE). */
export function invalidateAllShopKioskMetaCache(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({
    predicate: (query) => isShopKioskMetaQueryKey(query.queryKey),
  });
}

/**
 * Tenant-scoped shop kiosk meta refresh. When `salesPointId` is set, also
 * targets the exact `shopKioskById` key and any `gatewayMethods` probe for
 * that sales point (CARD rail after bank verify).
 */
export function invalidateShopKioskMetaCache(
  queryClient: QueryClient,
  tenantCode: string,
  salesPointId?: number,
): void {
  void queryClient.invalidateQueries({
    queryKey: customerQueryKeys.shopKioskBySlugPrefix(tenantCode),
  });
  if (salesPointId !== undefined && Number.isFinite(salesPointId) && salesPointId > 0) {
    void queryClient.invalidateQueries({
      queryKey: customerQueryKeys.shopKioskById(tenantCode, salesPointId),
    });
    void queryClient.invalidateQueries({
      queryKey: customerQueryKeys.gatewayMethods(tenantCode, salesPointId),
    });
    return;
  }
  void queryClient.invalidateQueries({
    queryKey: customerQueryKeys.shopKioskByIdPrefix(tenantCode),
  });
}

/**
 * Cross-tenant sales-point scoped refresh when directory SSE carries
 * `salesPointId` but no `tenantCode` (CustomerDirectoryGlobalSync).
 */
export function invalidateShopKioskMetaForSalesPoint(
  queryClient: QueryClient,
  salesPointId: number,
): void {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      if (!isShopKioskMetaQueryKey(key)) {
        return key[0] === 'customer' && key[2] === 'gatewayMethods' && key[3] === salesPointId;
      }
      return key[3] === 'kioskById' && key[4] === salesPointId;
    },
  });
}
